/**
 * Plan & Quotas Step (Step 3)
 *
 * Configures tenant resource limits, allowed environments,
 * and feature toggles.
 */

import type { ReactElement, ChangeEvent } from "react";
import { useCallback } from "react";

import type { TenantFeatures, TenantQuotas } from "../../pages/admin/tenants/types";
import { FEATURE_LABELS, FEATURE_DESCRIPTIONS } from "../../pages/admin/tenants/types";
import type { Environment } from "../../types";

export interface PlanQuotasData {
  readonly quotas: TenantQuotas;
  readonly allowedEnvironments: readonly Environment[];
  readonly features: TenantFeatures;
}

interface PlanQuotasStepProps {
  readonly data: PlanQuotasData;
  readonly onChange: (data: PlanQuotasData) => void;
}

const ENVIRONMENT_OPTIONS: readonly { value: Environment; label: string }[] = [
  { value: "dev", label: "Development" },
  { value: "staging", label: "Staging" },
  { value: "prod", label: "Production" },
];

export function PlanQuotasStep({ data, onChange }: PlanQuotasStepProps): ReactElement {
  const handleQuotaChange = useCallback(
    (quota: keyof TenantQuotas, value: string): void => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        onChange({
          ...data,
          quotas: {
            ...data.quotas,
            [quota]: numValue,
          },
        });
      }
    },
    [data, onChange]
  );

  const handleEnvironmentToggle = useCallback(
    (env: Environment): void => {
      const current = [...data.allowedEnvironments];
      const index = current.indexOf(env);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(env);
      }
      onChange({ ...data, allowedEnvironments: current });
    },
    [data, onChange]
  );

  const handleFeatureChange = useCallback(
    (feature: keyof TenantFeatures): void => {
      onChange({
        ...data,
        features: {
          ...data.features,
          [feature]: !data.features[feature],
        },
      });
    },
    [data, onChange]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Plan & Quotas</h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Configure resource limits, environments, and feature toggles for this tenant.
        </p>
      </div>

      {/* Quotas */}
      <div className="card">
        <h3 className="text-base font-semibold mb-4">Resource Limits</h3>
        <div className="grid grid-cols-2 gap-4">
          <QuotaInput
            id="maxVerticals"
            label="Max Verticals"
            value={data.quotas.maxVerticals}
            onChange={(v) => handleQuotaChange("maxVerticals", v)}
            min={1}
          />
          <QuotaInput
            id="maxProductsPerVertical"
            label="Max Products per Vertical"
            value={data.quotas.maxProductsPerVertical}
            onChange={(v) => handleQuotaChange("maxProductsPerVertical", v)}
            min={1}
          />
          <QuotaInput
            id="maxUsersPerTenant"
            label="Max Users"
            value={data.quotas.maxUsersPerTenant}
            onChange={(v) => handleQuotaChange("maxUsersPerTenant", v)}
            min={1}
          />
          <QuotaInput
            id="maxApiRequestsPerDay"
            label="Max API Requests/Day"
            value={data.quotas.maxApiRequestsPerDay}
            onChange={(v) => handleQuotaChange("maxApiRequestsPerDay", v)}
            min={0}
          />
          <QuotaInput
            id="maxStorageMb"
            label="Max Storage (MB)"
            value={data.quotas.maxStorageMb}
            onChange={(v) => handleQuotaChange("maxStorageMb", v)}
            min={0}
          />
        </div>
      </div>

      {/* Allowed Environments */}
      <div className="card">
        <h3 className="text-base font-semibold mb-4">Allowed Environments</h3>
        <p className="text-sm text-foreground-muted mb-3">
          Select which environments this tenant can access.
        </p>
        <div className="space-y-2">
          {ENVIRONMENT_OPTIONS.map((env) => (
            <label key={env.value} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.allowedEnvironments.includes(env.value)}
                onChange={() => handleEnvironmentToggle(env.value)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm">{env.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="card">
        <h3 className="text-base font-semibold mb-4">Features</h3>
        <p className="text-sm text-foreground-muted mb-3">
          Enable or disable features for this tenant.
        </p>
        <div className="space-y-3">
          {(Object.keys(data.features) as (keyof TenantFeatures)[]).map((feature) => (
            <label key={feature} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.features[feature]}
                onChange={() => handleFeatureChange(feature)}
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <div>
                <p className="text-sm font-medium">{FEATURE_LABELS[feature]}</p>
                <p className="text-xs text-foreground-muted">{FEATURE_DESCRIPTIONS[feature]}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Internal Components
// ============================================================================

interface QuotaInputProps {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: string) => void;
  readonly min: number;
}

function QuotaInput({ id, label, value, onChange, min }: QuotaInputProps): ReactElement {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={min}
        value={value}
        onChange={handleChange}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}
